import { LABELS } from '../constants';
import defaultTranslations from './en.json';

export type FormatTextSpan = {
    start: number;
    end: number;
    type: string;
    data?: {
        link_type?: string;
        target?: string;
        url?: string;
    };
};

type Options = {
    text: string;
    n?: number;
    brand?: string;
    product?: string;
    params?: (string | number)[];
};

export interface FormatTextProps {
    text: string;
    spans?: FormatTextSpan[];
    currencySymbol?: string;
    type?: string;
    className?: string;
}

const flattenObject = (object: any) => {
    const toReturn: Record<string, string> = {};

    for (const i in object) {
        if (typeof object[i] === 'object' && object[i] !== null) {
            const flatObject = flattenObject(object[i]);
            for (const x in flatObject) {
                toReturn[`${i}.${x}`] = flatObject[x];
            }
        } else {
            toReturn[i] = object[i];
        }
    }
    return toReturn;
};

const translations = flattenObject(defaultTranslations);

const CURRENCY_LABEL_REGEX = new RegExp(LABELS.STRING.CURRENCY, 'g');

const TAGS_TO_REPLACE: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;'
};

const TYPE_HYPERLINK = 'hyperlink';
const LINK_TYPE_MEDIA = 'media';
const SANITIZE_TAGS_ATTRIBUTES = /<([a-z][a-z0-9]*)(?:[^>]*(\scolspan=['"][^'"]*['"]))?[^>]*?>/gi;
const SANITIZE_SCRIPT_STYLE_REGEX = /<(script|style)([\s\S]*?)>([\s\S]*?)(<\/\1>)/gim;
const SANITIZE_IMG_TAG_REGEX = /<img([\s\S]*?)>/gim;

// Get item URL based on link_type value
const getItemUrl = (item: FormatTextSpan): string => {
    const linkType = item?.data?.link_type || '';
    switch (linkType.toLowerCase()) {
        default:
            return item?.data?.url || '';
    }
};

// Convert extract HTML tag and attributes from a prismic span
const getTag = (item: FormatTextSpan): [string, string] => {
    switch (item.type) {
        case TYPE_HYPERLINK: {
            const attributes: string[] = [''];
            const linkType = item?.data?.link_type || '';
            if (item?.data?.target) {
                attributes.push(`target='${item.data.target}'`);
            } else if (linkType.toLowerCase() === LINK_TYPE_MEDIA) {
                /* eslint-disable quotes */
                attributes.push("target='_blank'");
            }
            attributes.push(`href='${getItemUrl(item)}'`);

            return ['a', attributes.join(' ')];
        }
        default:
            return [item.type, ''];
    }
};

// Convert array with spans into an HTML string. Replace labels: currency (#cy#).
// The type from object span will be converted into HTML (type:"strong" => <strong>)
export const formatText = ({ text, spans = [], currencySymbol }: FormatTextProps): string => {
    const chars: string[] = [];

    // Sanitize text
    for (let i = 0; i < text.length; i++) {
        chars.push(text[i].replace(/[&<>]/g, (tag) => TAGS_TO_REPLACE[tag]));
    }

    spans.forEach((item) => {
        if (item.start >= 0 && item.end <= chars.length && item.start < item.end) {
            const [type, attributes] = getTag(item); // You'll need to define this function or update its type
            const startChar = chars[item.start];
            const itemEnd = item.end - 1;

            chars[item.start] = `<${type}${attributes}>${startChar}`;
            const endChar = chars[itemEnd];
            chars[itemEnd] = `${endChar}</${type}>`;
        }
    });

    let output = chars.join('');

    // Currency symbol replacement
    output = output.replace(CURRENCY_LABEL_REGEX, currencySymbol || '');

    return output || '<br>';
};

/**
 * Sanitize HTML string by removing:
 * - <script> and <style> tags
 * - img tags
 * - unsafe attributes like onclick, style, etc.
 *
 * @param htmlString - the raw HTML string
 * @returns sanitized HTML string
 */
export const sanitizeHtmlString = (htmlString: string): string => {
    return htmlString
        .replace(SANITIZE_TAGS_ATTRIBUTES, '<$1$2>')
        .replace(SANITIZE_SCRIPT_STYLE_REGEX, '')
        .replace(SANITIZE_IMG_TAG_REGEX, '');
};

const applyParams = (template: string, ...params: (string | number)[]): string => {
    let paramIndex = 0;
    return template.replace(/%[sd]/g, (match) => {
        if (paramIndex >= params.length) {
            throw new Error('Not enough parameters provided for format string');
        }

        const param = params[paramIndex++];

        if (match === '%d') {
            if (typeof param !== 'number') {
                throw new Error(`Expected number for %d but got ${typeof param}`);
            }
            return param.toString();
        } else if (match === '%s') {
            return String(param);
        }

        return match;
    });
};

export const getText = ({
    text,
    n,
    brand = process.env.BRAND,
    product = process.env.PRODUCT,
    params = []
}: Options) => {
    const string =
        (translations as Record<string, any>)?.[`${text}.${brand}.forms.${n}`] ||
        (translations as Record<string, any>)?.[`${text}.default.forms.${n}`] ||
        (translations as Record<string, any>)?.[`${text}.forms.${n}`] ||
        (translations as Record<string, any>)?.[`${text}.${brand}.text`] ||
        (translations as Record<string, any>)?.[`${text}.${brand}.${product}.text`] ||
        (translations as Record<string, any>)?.[`${text}.${brand}.${product}`] ||
        (translations as Record<string, any>)?.[`${text}.${brand}.default.text`] ||
        (translations as Record<string, any>)?.[`${text}.${brand}.default`] ||
        (translations as Record<string, any>)?.[`${text}.${brand}`] ||
        (translations as Record<string, any>)?.[`${text}.default.text`] ||
        (translations as Record<string, any>)?.[`${text}.default`] ||
        (translations as Record<string, any>)?.[`${text}.text`] ||
        (translations as Record<string, any>)?.[`${text}`] ||
        '';

    if (params.length > 0) {
        return applyParams(string, ...params);
    }

    return string;
};

/**
 * Convert a string to lower snake_case
 * Example: "Game Name" -> "game_name"
 * @param str - the string to convert
 * @returns snake_case string
 */
export const toSnakeCase = (str: string): string => {
    return str
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_')
        .replace(/[^\w_]/g, '');
};
